import { Grid2 as Grid, Box, Typography } from '@mui/material';
import useBillingService, { ApproveBillingData, BillingScheduleItemResult } from '../billingService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  useNewProjectUrl,
  ACCESS_INFO_SECTIONS,
  ACCESS_INFO_ACTIONS,
  BillingType,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { Modal } from 'framework/ui/ui-builder';
import { ApprovalLayout } from 'framework/ui/ui-builder/approval';
import React, { useState } from 'react';
import { ModalForm, TextArea } from 'framework/form';
import { useShowContext } from 'framework/show';
import { EmployeeLinkWithNameLoading } from 'domain/shared/employee';
import { AttachmentsListField, SearchResultAttachmentsListField } from 'domain/shared/artifact/field';
import { EMPTY_VALUE_INDICATOR, SanitizedHtml } from 'framework/utils';
import { Alert, Button, Loading } from 'framework/ui/mui-builder';
import { BillingRequestStatus } from '../billingTypes';
import { useBillingRouteContext } from '../BillingRouteContext';
import { SuccessMessage, useMessageService } from 'framework/ui/Message';
import { LabelValue } from './components';
import { getApproveBillingColumns, sortByPosition } from './ApproveBillingUtils';

const ApproveBillingContent = ({ refetch }: { refetch: () => void }) => {
  const { data, setData } = useShowContext<ApproveBillingData>();
  const { addMessage } = useMessageService();

  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasApproveError, setHasApproveError] = useState(false);
  const [hasRejectError, setHasRejectError] = useState(false);

  const { billingScheduleId, billingRequestId } = useParams<Dictionary<string>>();
  const { approveBillingRequest, rejectBillingRequest, getBillingRequestRejectValidationSchema } = useBillingService();
  const { getFinanceUrl, getUpdateBillingRequestUrl } = useNewProjectUrl();
  const navigate = useNavigate();
  const { isAccessGranted } = useProjectSecurityContext();
  const { projectId } = useBillingRouteContext();

  const location = useLocation();
  const billingData = data.getData();
  const hasReviewPermission = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.REVIEW);
  const hasManagePermission = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.MANAGE);
  const isPreviewMode = location.pathname.includes('/preview');
  const isBilled = billingData?.status === BillingRequestStatus.BILLED;
  const billingRequestIdFromData = billingData?.items?.[0]?.billingRequestId || billingRequestId;
  const isPrepaid = billingData?.type === BillingType.PERIODIC_PREPAID;

  if (!billingData) {
    return null;
  }

  const {
    items = [],
    project,
    sowId,
    note,
    serviceDetailsArtifactId,
    reimbursementDocumentationArtifactIds,
    startDate,
    endDate,
    relatedDocuments,
    budgetId,
    rejectionReason,
    status,
  } = billingData;
  const isRejected = status === BillingRequestStatus.REJECTED;

  const handleApprove = async () => {
    if (!billingRequestIdFromData) return;
    setHasApproveError(false);
    setIsSubmitting(true);
    try {
      const approvePayload = serviceDetailsArtifactId
        ? {
            serviceDetailsArtifactId,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          }
        : {};

      await approveBillingRequest(billingScheduleId!, billingRequestIdFromData, approvePayload);
      addMessage(new SuccessMessage('You are awesome! Billing request has been approved successfully.'));
      const response = await refetch();
      setData!(response);
    } catch {
      setHasApproveError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (values: Dictionary<string>) => {
    if (!billingRequestIdFromData) return;
    setHasRejectError(false);
    await rejectBillingRequest(billingScheduleId!, billingRequestIdFromData, values.reason);

    addMessage(new SuccessMessage('You are awesome! Billing request has been rejected successfully.'));

    const response = await refetch();
    setData!(response);
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const currency = items[0]?.currency || '';
  const isPlannedHours = items.some(item => item.plannedRegularHours);

  const columns = getApproveBillingColumns(isPrepaid, isPlannedHours);
  const totalSize = columns.reduce((sum, column) => sum + column.size, 0);
  const totalColumn = columns.find(column => column.renderTotal);
  const TABLE_CELL_BORDER = '1px solid var(--mui-palette-TableCell-border)';

  const canDecide = hasReviewPermission && !isPreviewMode && !isBilled && !isRejected;

  const rejectedActions =
    isPreviewMode && hasManagePermission && isRejected && !isBilled ? (
      <>
        <Button variant="outlined" onClick={() => navigate(getFinanceUrl(projectId))}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (billingScheduleId && billingRequestId) {
              navigate(getUpdateBillingRequestUrl({ billingScheduleId, billingRequestId }));
            }
          }}
        >
          Edit
        </Button>
      </>
    ) : undefined;

  return (
    <ApprovalLayout
      title={isPreviewMode ? 'Billing request' : 'Approve billing request'}
      titleVariant="h2"
      onApprove={canDecide ? handleApprove : undefined}
      onReject={
        canDecide
          ? () => {
              setHasRejectError(false);
              setModalOpen(true);
            }
          : undefined
      }
      rejectLabel="Request clarification"
      isSubmitting={isSubmitting}
      actions={rejectedActions}
    >
      {hasApproveError && <Alert severity="error">Failed to approve billing request. Please contact support.</Alert>}
      {isSubmitting && <Loading hasBackground />}
      {isRejected && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            my: 2,
            pb: 2,
            color: 'error.main',
          }}
        >
          <Typography component="span" sx={{ mr: 1 }}>
            Was rejected with reason:
          </Typography>
          <SanitizedHtml wrapperTag="span" html={rejectionReason || ''} />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2, maxWidth: 400 }}>
        <LabelValue label="Project:" value={project?.name || EMPTY_VALUE_INDICATOR} />
        <LabelValue
          label="Project manager:"
          value={
            project?.projectManager ? (
              <EmployeeLinkWithNameLoading id={project.projectManager} />
            ) : (
              EMPTY_VALUE_INDICATOR
            )
          }
        />
        <LabelValue
          label="Current SOW:"
          value={
            relatedDocuments && relatedDocuments.length > 0 ? (
              <SearchResultAttachmentsListField source="" value={relatedDocuments} budgetId={budgetId} />
            ) : sowId ? (
              `SOW${sowId}`
            ) : (
              EMPTY_VALUE_INDICATOR
            )
          }
        />
      </Box>
      <Box sx={{ maxWidth: '100%', overflowX: 'auto', marginTop: '40px', marginBottom: '30px' }}>
        <Grid container sx={{ width: 'fit-content', minWidth: 595, border: TABLE_CELL_BORDER }}>
          {columns.map((column, columnIndex) => (
            <Grid
              key={column.id}
              size={column.size}
              sx={{
                borderRight: columnIndex < columns.length - 1 ? TABLE_CELL_BORDER : 'none',
                borderBottom: TABLE_CELL_BORDER,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary" textAlign="center">
                {column.label}
              </Typography>
            </Grid>
          ))}

          {[...items].sort(sortByPosition).map((item: BillingScheduleItemResult, rowIndex: number) => (
            <React.Fragment key={item.id || rowIndex}>
              {columns.map((column, columnIndex) => (
                <Grid
                  key={`${item.id || rowIndex}-${column.id}`}
                  size={column.size}
                  sx={{
                    borderRight: columnIndex < columns.length - 1 ? TABLE_CELL_BORDER : 'none',
                    borderBottom: rowIndex < items.length - 1 ? TABLE_CELL_BORDER : 'none',
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" textAlign="center">
                    {column.renderCell(item)}
                  </Typography>
                </Grid>
              ))}
            </React.Fragment>
          ))}
          {items.length > 1 && totalColumn && (
            <React.Fragment>
              <Grid
                size={totalSize - totalColumn.size}
                sx={{
                  borderRight: TABLE_CELL_BORDER,
                  borderTop: TABLE_CELL_BORDER,
                  p: 1.5,
                }}
              >
                <Typography variant="subtitle1">Total</Typography>
              </Grid>
              <Grid size={totalColumn.size} sx={{ borderTop: TABLE_CELL_BORDER, p: 1.5 }}>
                <Typography variant="subtitle1">{totalColumn.renderTotal?.(totalAmount, currency)}</Typography>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </Box>

      {note && (
        <Box sx={{ mb: 3.5 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Note:
          </Typography>
          <SanitizedHtml html={note} />
        </Box>
      )}

      {serviceDetailsArtifactId && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Service details:
          </Typography>
          <AttachmentsListField source="" value={[serviceDetailsArtifactId]} />
        </Box>
      )}

      {reimbursementDocumentationArtifactIds && reimbursementDocumentationArtifactIds.length > 0 && (
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Reimbursement documentation:
          </Typography>
          <AttachmentsListField source="" value={reimbursementDocumentationArtifactIds} />
        </Box>
      )}

      <Modal isOpen={modalOpen} onToggle={() => setModalOpen(false)} title="Reason for rejection">
        <ModalForm
          closeModal={() => setModalOpen(false)}
          modalOpen={modalOpen}
          getValidationSchemaFunc={getBillingRequestRejectValidationSchema}
          submitFormFunc={handleReject}
          handleErrorsOnSubmit={() => setHasRejectError(true)}
        >
          {hasRejectError && (
            <Alert severity="error">Failed to process clarification request. Please contact support.</Alert>
          )}
          <TextArea source="reason" label="" placeholder="Please, provide reason here" />
        </ModalForm>
      </Modal>
    </ApprovalLayout>
  );
};

export default ApproveBillingContent;
